import { ReactNode, useCallback } from 'react';

import { ChatResponse } from '@ariksa/ai-engine/api';
import {
  Avatar,
  Box,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { dispatch } from 'd3';
import { entries, isEmpty, slice } from 'lodash';
import map from 'lodash/map';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useInventoryInsightService } from 'services/Inventory/useInventoryInsightService';
import { customTheme } from 'theme';

import { Tag } from 'components/DataDisplay';
import ChakraMarkdown from 'components/DataDisplay/Markdown/ChakraMarkdown';
import { MonitoringIcon, PolicyIcon } from 'components/Icons';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { AIReferenceCard } from 'containers/ConversationalAI/Components/AiAssistant/References/AIReferenceCard';
import { ReferenceWrapper } from 'containers/ConversationalAI/Components/AiAssistant/References/ReferenceWrapper';
import { actions } from 'containers/ConversationalAI/slice';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

interface BotResponseProps {
  item: ChatResponse & {
    query?: string;
    ai_search?: string;
    references: Record<any, any>[];
  };
  setTabIndex(val: number);
}

export const Assistant = (props: BotResponseProps) => {
  const {
    metadata,
    output,
    query,
    ui_redirection_link,
    references,
  } = props.item;
  const navigate = useNavigate();
  const showQuery = useDisclosure();
  const dispatch = useDispatch();
  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();

  const onClickLink = useCallback(
    (id: string, type: string) => () => {
      dispatch(
        actions.getResourceInsight({
          q: {
            insightV2Request: {
              ...(type === 'arn' ? { arns: [id] } : { resource_ids: [id] }),
            },
          },
          onSuccess: res => {
            const data = res?.items?.[0];
            if (!isEmpty(data)) {
              updateActiveResource({
                resourceId: data?.resource_id,
                resourceType: data?.native_name,
                uuid: data?.uuid,
                accountId: data?.account,
                cloudProvider: data?.provider,
              });
              onOpenMetadata();
            }
          },
        }),
      );
    },
    [],
  );

  const sanitizeQuery = (query: string) => {
    return entries({
      SHOW: 'Show',
      THAT: 'That',
    }).reduce((acc, [k, v]) => {
      return acc?.replace(k, v);
    }, query);
  };

  const renderReferences = () =>
    map(references, o => <AIReferenceCard url={o?.url} title={o?.title} />);

  const renderReferencesCount = () => {
    const count = references.length - 3;
    if (!count) return null;

    const getfaviconUrl = (url: string) => {
      const domain = new URL(url).hostname.replace(/^www\./, '');
      return `https://www.google.com/s2/favicons?domain=${domain}`;
    };

    return (
      <ReferenceWrapper onClick={() => props.setTabIndex(1)} flex={1} py={3}>
        <Stack>
          <HStack>
            {map(slice(references, 3, 6), o => (
              <Avatar src={getfaviconUrl(o?.url)} size="2xs" />
            ))}
          </HStack>
          <Box color={customTheme.colors.gray['300']} fontSize="sm">
            +{count} more
          </Box>
        </Stack>
      </ReferenceWrapper>
    );
  };

  return (
    <Stack spacing={6} w="full">
      {!!references?.length && (
        <Box>
          <HStack w="full" overflowX="scroll">
            {renderReferences()}
          </HStack>
        </Box>
      )}

      <Stack spacing={4}>
        <Stack px={4} fontSize={'md'}>
          <ActiveResourceProvider>
            <ChakraMarkdown content={output} onClickLink={onClickLink} />
          </ActiveResourceProvider>
        </Stack>

        <HStack spacing={4} pt={4}>
          {ui_redirection_link?.investigate && (
            <ResponseTag
              label={'Investigate'}
              leftIcon={<MonitoringIcon />}
              onClick={() => {
                const url =
                  ui_redirection_link?.investigate + '&redirected=true';
                navigate(url, {
                  state: { wrapper: metadata?.investigate?.wrapper },
                });
              }}
            />
          )}
          {!showQuery.isOpen && !!query && !!metadata?.investigate?.generated && (
            <ResponseTag
              label={'Create Policy'}
              leftIcon={<PolicyIcon />}
              onClick={() => {
                const url =
                  '/policy-hub/policy/cloud/add?query=' +
                  metadata?.investigate?.query +
                  '&resource=' +
                  metadata?.investigate?.wrapper?.resource?.name;
                navigate(url, {
                  state: { wrapper: metadata?.investigate?.wrapper },
                });
              }}
            />
          )}
        </HStack>
        {showQuery.isOpen && (
          <Text fontWeight={'bold'} fontSize={'md'}>
            {sanitizeQuery(query ?? '')}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

const ResponseTag = (props: {
  label: string;
  leftIcon?: ReactNode;
  onClick: Function;
}) => {
  const leftIcon = props.leftIcon ? props.leftIcon : null;
  const onClick = props.onClick;
  return (
    <Tag
      label={props.label}
      styles={{
        tag: {
          cursor: 'pointer',
          borderColor: 'rgba(93,62,219,0.4)',
          size: 'md',
          fontSize: 'sm',
        },
        leftIcon: { mr: 2 },
      }}
      leftIcon={leftIcon}
      onClick={() => {
        onClick?.();
      }}
    />
  );
};
